import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { useDashboard } from "@/contexts/DashboardContext";
import { SlaRankingRow } from "@/lib/axios/types/widgets.type";
import { formatPercentage } from "@/lib/utils";
import { calculateCustomDateRange } from "@/utils/dateRangeUtils";
import { fetchSlaRankingPaged } from "@/lib/axios/api/widgets.api";
import { PaginatedResponse } from "@/lib/axios/types";
import { ChevronLeft, ChevronRight, Info, TrendingDown, TrendingUp } from "lucide-react";
import React from "react";
import AgentBarRow from "../common/AgentBarRow";

interface SlaRankingWidgetProps {
  startDate: string;
  endDate: string;
  previousStartDate?: string;
  previousEndDate?: string;
  queueMicrosoftIds?: string[] | null;
  agentMicrosoftIds?: string[] | null;
  groupIds?: number[] | null;
  unifiedChartHeight?: number;
  agentLimit?: number;
}

const SlaRankingWidget: React.FC<SlaRankingWidgetProps> = ({
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  queueMicrosoftIds,
  agentMicrosoftIds,
  groupIds,
  unifiedChartHeight,
  agentLimit = 20
}) => {
  const { theme } = useDashboard();
  const [sortOrder, setSortOrder] = React.useState<'desc' | 'asc'>("desc");
  const [page, setPage] = React.useState<number>(1);
  const [data, setData] = React.useState<PaginatedResponse<SlaRankingRow> | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  // Constants
  const limit = 10;
  const MAX_ROWS_FOR_CARD = 10;
  // Fetch data effect
  React.useEffect(() => {
    const controller = new AbortController();
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(undefined);

    const prev = previousStartDate && previousEndDate
      ? { previousFrom: new Date(previousStartDate), previousTo: new Date(previousEndDate) }
      : calculateCustomDateRange(new Date(startDate), new Date(endDate));

    fetchSlaRankingPaged({
      startDate,
      endDate,
      previousStartDate: prev.previousFrom?.toISOString(),
      previousEndDate: prev.previousTo?.toISOString(),
      queueMicrosoftIds: queueMicrosoftIds ?? null,
      agentMicrosoftIds: agentMicrosoftIds ?? null,
      groupIds: groupIds ?? null,
      page,
      limit,
      sort: sortOrder,
    }, { signal: controller.signal })
      .then(setData)
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setError(err.message || 'Failed to fetch data')
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [startDate, endDate, previousStartDate, previousEndDate, queueMicrosoftIds, agentMicrosoftIds, groupIds, page, sortOrder]);

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [startDate, endDate, queueMicrosoftIds, agentMicrosoftIds, groupIds]);

  const MIN_LABEL_PX = 28; // space needed to fit text like "10%" inside

  // Memoized calculations
  const { visibleData, totalItems, totalPages, startIndex, endIndex, contentHeight } = React.useMemo(() => {
    const rowsOnPage = Math.min(limit, data?.data?.length || 0);
    const heightRows = loading ? MAX_ROWS_FOR_CARD : (rowsOnPage || 1);
    const height = unifiedChartHeight ?? Math.max(300, Math.min(560, heightRows * 40 + 80));

    const items = data?.total || 0;
    const pages = Math.max(1, Math.ceil(items / limit));
    const startIdx = (page - 1) * limit + 1;
    const endIdx = Math.min(page * limit, items);

    return {
      visibleData: data?.data || [],
      totalItems: items,
      totalPages: pages,
      startIndex: startIdx,
      endIndex: endIdx,
      contentHeight: height
    };
  }, [data, page, limit, loading, unifiedChartHeight]);

  // Refs and state
  const chartRef = React.useRef<HTMLDivElement | null>(null);
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const barContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [barContainerWidth, setBarContainerWidth] = React.useState<number>(0);
  const [mouseTip, setMouseTip] = React.useState<{
    visible: boolean;
    x: number;
    y: number;
    content: React.ReactNode;
  }>({ visible: false, x: 0, y: 0, content: null });

  // Measure bar container width for label positioning
  React.useEffect(() => {
    if (!barContainerRef.current) return;
    const el = barContainerRef.current;
    const measure = () => setBarContainerWidth(el.offsetWidth || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Tooltip positioning utility
  const computeTooltipPosition = React.useCallback((clientX: number, clientY: number) => {
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = tooltipRef.current?.offsetWidth ?? 220;
    const height = tooltipRef.current?.offsetHeight ?? 80;

    let x = clientX + margin;
    let y = clientY + margin;

    if (x + width > vw - 8) x = clientX - width - margin;
    if (y + height > vh - 8) y = Math.max(8, vh - height - 8);

    return { x, y };
  }, []);

  // Pagination component
  const renderPagination = React.useCallback(() => (
    <div className="flex items-center justify-between w-full">
      <div className="text-xs text-muted-foreground">
        Showing {startIndex}-{endIndex} of {totalItems}
      </div>
      <div className="flex items-center gap-2">
        <button
          className="w-8 h-8 grid place-items-center rounded border disabled:opacity-50"
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1 || loading}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs px-1">{page} / {totalPages}</span>
        <button
          className="w-8 h-8 grid place-items-center rounded border disabled:opacity-50"
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages || loading}
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  ), [startIndex, endIndex, totalItems, page, totalPages, loading]);

  return (
    <Card className="w-full overflow-hidden h-full bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] flex flex-col">
      <CardHeader className="dark:border-b flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span>SLA (%) Ranking</span>
            <InfoTooltip
              content="The percentage of total incoming calls handled within 60 seconds among agents within the given time."
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 bg-white dark:bg-[#0C3451] dark:text-white border-t dark:border-[#353535] flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-auto">
          <div className="flex items-center justify-end gap-2 p-3 pb-0">
            {React.useMemo(() => {
              const inactiveBtn = "flex items-center gap-2 px-2 py-1 text-sm rounded-sm border border-input no-underline transition-colors bg-white text-primary hover:bg-[#00FEC1] hover:text-black hover:border-transparent dark:bg-[#0C3451] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:text-black";
              const activeBtn = "flex items-center gap-2 px-2 py-1 text-sm rounded-sm border border-transparent no-underline bg-[#00FEC1] text-black";

              const handleSortChange = (order: 'desc' | 'asc') => {
                setPage(1);
                setSortOrder(order);
              };

              return (
                <>
                  <button
                    type="button"
                    aria-pressed={sortOrder === 'desc'}
                    onClick={() => handleSortChange('desc')}
                    className={sortOrder === 'desc' ? activeBtn : inactiveBtn}
                  >
                    <TrendingUp className="w-4 h-4" />
                    Highest
                  </button>
                  <button
                    type="button"
                    aria-pressed={sortOrder === 'asc'}
                    onClick={() => handleSortChange('asc')}
                    className={sortOrder === 'asc' ? activeBtn : inactiveBtn}
                  >
                    <TrendingDown className="w-4 h-4" />
                    Lowest
                  </button>
                </>
              );
            }, [sortOrder])}
          </div>

          {React.useMemo(() => {
            // Loading state
            if (loading) {
              return (
                <div className="p-3 pb-0 pointer-events-none flex-1" style={{ minHeight: contentHeight }}>
                  <div className="space-y-2">
                    {Array.from({ length: MAX_ROWS_FOR_CARD }).map((_, idx) => (
                      <div key={idx} className="flex items-center py-1.5">
                        <div className="w-32 pr-4 flex-shrink-0">
                          <Skeleton className="h-3 w-24" />
                        </div>
                        <div className="flex-1">
                          <div className="h-6 rounded bg-gray-200 dark:bg-white/10 overflow-hidden">
                            <div
                              className="h-full animate-pulse bg-gray-300 dark:bg-white/20"
                              style={{ width: `${30 + (idx % 5) * 12}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            // No data state
            if (!data?.data || data.data.length === 0) {
              return (
                <div className="p-3 pb-0 flex items-center justify-center" style={{ minHeight: contentHeight }}>
                  <div className="flex items-center justify-center h-full text-sm text-muted-foreground dark:text-white/60">
                    <div className="flex flex-col items-center gap-2">
                      <span>No call records were found</span>
                    </div>
                  </div>
                </div>
              );
            }

            // Data rendering
            const barColor = theme === 'dark' ? '#60a5fa' : '#1681f6';
            return (
              <div
                ref={chartRef}
                className="p-3 flex-1"
                style={{
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  position: 'relative'
                }}
              >
                <div className="space-y-2">
                  {visibleData.map((row, idx) => {
                    const width = Math.max(0, Math.min(100, row.sla));
                    const barWidthPx = barContainerWidth ? (barContainerWidth * width) / 100 : 0;
                    const showTextInside = barContainerWidth
                      ? barWidthPx >= MIN_LABEL_PX
                      : width > 8;

                    return (
                      <AgentBarRow
                        key={`${row.agent}-${idx}`}
                        name={row.agent}
                        right={
                          <div className="flex items-center">
                            <div
                              className={`w-full h-6 bg-gray-200 rounded overflow-hidden relative ${row.sla > 0 ? 'cursor-pointer' : 'cursor-default'
                                }`}
                              ref={idx === 0 ? barContainerRef : undefined}
                              onMouseEnter={(e) => {
                                if (row.sla <= 0) return;
                                const pos = computeTooltipPosition(e.clientX, e.clientY);
                                setMouseTip({
                                  visible: true,
                                  x: pos.x,
                                  y: pos.y,
                                  content: <div className="text-xs">SLA: {formatPercentage(row.sla)}</div>
                                });
                              }}
                              onMouseMove={(e) => {
                                if (!mouseTip.visible) return;
                                const pos = computeTooltipPosition(e.clientX, e.clientY);
                                setMouseTip((prev) => ({ ...prev, x: pos.x, y: pos.y }));
                              }}
                              onMouseLeave={() => setMouseTip({ visible: false, x: 0, y: 0, content: null })}
                            >
                              <div
                                className="h-full flex items-center justify-center text-white text-[10px] md:text-[10px] font-medium"
                                style={{ width: `${width}%`, background: barColor }}
                              >
                                {showTextInside ? formatPercentage(row.sla) : ''}
                              </div>
                              {!showTextInside && row.sla > 0 && (
                                <span
                                  className="absolute top-0 bottom-0 flex items-center text-[10px] text-gray-700 dark:text-white"
                                  style={{ left: `calc(${width}% + 6px)` }}
                                >
                                  {formatPercentage(row.sla)}
                                </span>
                              )}
                            </div>
                          </div>
                        }
                      />
                    );
                  })}
                </div>
                {mouseTip.visible && (
                  <div
                    ref={tooltipRef}
                    className="fixed z-50 pointer-events-none border rounded-md shadow-md p-2 text-xs bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                    style={{ left: mouseTip.x, top: mouseTip.y }}
                  >
                    {mouseTip.content}
                  </div>
                )}
              </div>
            );
          }, [loading, data, contentHeight, theme, visibleData, barContainerWidth, computeTooltipPosition, mouseTip])}
        </div>
        {!loading && data?.data && data.data.length > 0 && (
          <div className="border-t p-3 flex-shrink-0 dark:border-white">
            {renderPagination()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SlaRankingWidget;
